import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../services/api';

export const fetchFlows = createAsyncThunk(
  'whatsappFlows/fetchFlows',
  async (filters, { rejectWithValue }) => {
    try {
      const res = await api.get('/whatsapp-flows', { params: filters });
      return res.data.flows;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to fetch flows');
    }
  }
);

export const createFlow = createAsyncThunk(
  'whatsappFlows/createFlow',
  async (data, { rejectWithValue }) => {
    try {
      const res = await api.post('/whatsapp-flows', data);
      return res.data.flow;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to create flow');
    }
  }
);

export const updateFlow = createAsyncThunk(
  'whatsappFlows/updateFlow',
  async ({ id, ...data }, { rejectWithValue }) => {
    try {
      const res = await api.put(`/whatsapp-flows/${id}`, data);
      return res.data.flow;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to update flow');
    }
  }
);

export const updateFlowJson = createAsyncThunk(
  'whatsappFlows/updateFlowJson',
  async ({ id, flowJson }, { rejectWithValue }) => {
    try {
      const res = await api.patch(`/whatsapp-flows/${id}/json`, { flowJson });
      return res.data.flow;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to update flow JSON');
    }
  }
);

export const publishFlow = createAsyncThunk(
  'whatsappFlows/publishFlow',
  async (id, { rejectWithValue }) => {
    try {
      const res = await api.post(`/whatsapp-flows/${id}/publish`);
      return res.data.flow;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to publish flow');
    }
  }
);

export const deprecateFlow = createAsyncThunk(
  'whatsappFlows/deprecateFlow',
  async (id, { rejectWithValue }) => {
    try {
      const res = await api.post(`/whatsapp-flows/${id}/deprecate`);
      return res.data.flow;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to deprecate flow');
    }
  }
);

export const deleteFlow = createAsyncThunk(
  'whatsappFlows/deleteFlow',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/whatsapp-flows/${id}`);
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to delete flow');
    }
  }
);

export const getPreview = createAsyncThunk(
  'whatsappFlows/getPreview',
  async (id, { rejectWithValue }) => {
    try {
      const res = await api.get(`/whatsapp-flows/${id}/preview`);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to get preview');
    }
  }
);

export const generateKeyPair = createAsyncThunk(
  'whatsappFlows/generateKeyPair',
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.post('/whatsapp-flows/keys/generate');
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to generate key pair');
    }
  }
);

export const getPublicKey = createAsyncThunk(
  'whatsappFlows/getPublicKey',
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get('/whatsapp-flows/keys/public');
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to get public key');
    }
  }
);

export const sendFlow = createAsyncThunk(
  'whatsappFlows/sendFlow',
  async (data, { rejectWithValue }) => {
    try {
      const res = await api.post('/whatsapp-flows/send', data);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to send flow');
    }
  }
);

export const syncToMeta = createAsyncThunk(
  'whatsappFlows/syncToMeta',
  async (id, { rejectWithValue }) => {
    try {
      const res = await api.post(`/whatsapp-flows/${id}/sync`);
      return res.data.flow;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to sync flow to Meta');
    }
  }
);

export const saveFlowsToken = createAsyncThunk(
  'whatsappFlows/saveFlowsToken',
  async (token, { rejectWithValue }) => {
    try {
      const res = await api.post('/whatsapp-flows/keys/token', { flowsAccessToken: token });
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to save Flows API token');
    }
  }
);

export const uploadPublicKey = createAsyncThunk(
  'whatsappFlows/uploadPublicKey',
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.post('/whatsapp-flows/keys/upload');
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to upload public key to Meta');
    }
  }
);

export const fetchSessions = createAsyncThunk(
  'whatsappFlows/fetchSessions',
  async (filters, { rejectWithValue }) => {
    try {
      const res = await api.get('/whatsapp-flows/sessions', { params: filters });
      return res.data.sessions;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to fetch sessions');
    }
  }
);

const whatsappFlowsSlice = createSlice({
  name: 'whatsappFlows',
  initialState: {
    flows: [],
    sessions: [],
    loading: false,
    error: null,
    publicKey: null,
    previewUrl: null,
    uploadingKey: false,
  },
  reducers: {
    clearWhatsappFlowsError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      // fetchFlows
      .addCase(fetchFlows.pending,    (state) => { state.loading = true; state.error = null; })
      .addCase(fetchFlows.fulfilled,  (state, action) => { state.loading = false; state.flows = action.payload; })
      .addCase(fetchFlows.rejected,   (state, action) => { state.loading = false; state.error = action.payload; })

      // createFlow
      .addCase(createFlow.pending,    (state) => { state.loading = true; state.error = null; })
      .addCase(createFlow.fulfilled,  (state, action) => { state.loading = false; state.flows.push(action.payload); })
      .addCase(createFlow.rejected,   (state, action) => { state.loading = false; state.error = action.payload; })

      // updateFlow
      .addCase(updateFlow.pending,    (state) => { state.loading = true; state.error = null; })
      .addCase(updateFlow.fulfilled,  (state, action) => {
        state.loading = false;
        const idx = state.flows.findIndex(f => f._id === action.payload._id);
        if (idx !== -1) state.flows[idx] = action.payload;
      })
      .addCase(updateFlow.rejected,   (state, action) => { state.loading = false; state.error = action.payload; })

      // updateFlowJson
      .addCase(updateFlowJson.pending,   (state) => { state.loading = true; state.error = null; })
      .addCase(updateFlowJson.fulfilled, (state, action) => {
        state.loading = false;
        const idx = state.flows.findIndex(f => f._id === action.payload._id);
        if (idx !== -1) state.flows[idx] = action.payload;
      })
      .addCase(updateFlowJson.rejected,  (state, action) => { state.loading = false; state.error = action.payload; })

      // publishFlow
      .addCase(publishFlow.pending,   (state) => { state.loading = true; state.error = null; })
      .addCase(publishFlow.fulfilled, (state, action) => {
        state.loading = false;
        const idx = state.flows.findIndex(f => f._id === action.payload._id);
        if (idx !== -1) state.flows[idx] = action.payload;
      })
      .addCase(publishFlow.rejected,  (state, action) => { state.loading = false; state.error = action.payload; })

      // deprecateFlow
      .addCase(deprecateFlow.pending,   (state) => { state.loading = true; state.error = null; })
      .addCase(deprecateFlow.fulfilled, (state, action) => {
        state.loading = false;
        const idx = state.flows.findIndex(f => f._id === action.payload._id);
        if (idx !== -1) state.flows[idx] = action.payload;
      })
      .addCase(deprecateFlow.rejected,  (state, action) => { state.loading = false; state.error = action.payload; })

      // deleteFlow
      .addCase(deleteFlow.pending,    (state) => { state.loading = true; state.error = null; })
      .addCase(deleteFlow.fulfilled,  (state, action) => {
        state.loading = false;
        state.flows = state.flows.filter(f => f._id !== action.payload);
      })
      .addCase(deleteFlow.rejected,   (state, action) => { state.loading = false; state.error = action.payload; })

      // getPreview
      .addCase(getPreview.pending,    (state) => { state.loading = true; state.error = null; })
      .addCase(getPreview.fulfilled,  (state, action) => {
        state.loading = false;
        state.previewUrl = action.payload.previewUrl ?? action.payload;
      })
      .addCase(getPreview.rejected,   (state, action) => { state.loading = false; state.error = action.payload; })

      // generateKeyPair
      .addCase(generateKeyPair.pending,    (state) => { state.loading = true; state.error = null; })
      .addCase(generateKeyPair.fulfilled,  (state, action) => { state.loading = false; state.publicKey = action.payload; })
      .addCase(generateKeyPair.rejected,   (state, action) => { state.loading = false; state.error = action.payload; })

      // getPublicKey
      .addCase(getPublicKey.pending,    (state) => { state.loading = true; state.error = null; })
      .addCase(getPublicKey.fulfilled,  (state, action) => { state.loading = false; state.publicKey = action.payload; })
      .addCase(getPublicKey.rejected,   (state, action) => { state.loading = false; state.error = action.payload; })

      // syncToMeta
      .addCase(syncToMeta.pending,    (state) => { state.loading = true; state.error = null; })
      .addCase(syncToMeta.fulfilled,  (state, action) => {
        state.loading = false;
        if (action.payload) {
          const idx = state.flows.findIndex(f => f._id === action.payload._id);
          if (idx !== -1) state.flows[idx] = action.payload;
        }
      })
      .addCase(syncToMeta.rejected,   (state, action) => { state.loading = false; state.error = action.payload; })

      // uploadPublicKey
      .addCase(uploadPublicKey.pending,    (state) => { state.uploadingKey = true; state.error = null; })
      .addCase(uploadPublicKey.fulfilled,  (state) => { state.uploadingKey = false; })
      .addCase(uploadPublicKey.rejected,   (state, action) => { state.uploadingKey = false; state.error = action.payload; })

      // sendFlow
      .addCase(sendFlow.pending,    (state) => { state.loading = true; state.error = null; })
      .addCase(sendFlow.fulfilled,  (state) => { state.loading = false; })
      .addCase(sendFlow.rejected,   (state, action) => { state.loading = false; state.error = action.payload; })

      // fetchSessions
      .addCase(fetchSessions.pending,    (state) => { state.loading = true; state.error = null; })
      .addCase(fetchSessions.fulfilled,  (state, action) => { state.loading = false; state.sessions = action.payload; })
      .addCase(fetchSessions.rejected,   (state, action) => { state.loading = false; state.error = action.payload; });
  },
});

export const { clearWhatsappFlowsError } = whatsappFlowsSlice.actions;

export const selectFlows         = (state) => state.whatsappFlows.flows;
export const selectSessions      = (state) => state.whatsappFlows.sessions;
export const selectFlowsLoading  = (state) => state.whatsappFlows.loading;

export default whatsappFlowsSlice.reducer;
